import { useEffect, useCallback } from 'react';
import { useUIStore } from '@/stores';
import { CardColor } from '@/types/card';
import './ColorPicker.css';

/**
 * ColorPicker - ワイルドカード色選択モーダル
 * ワイルドカードをプレイした際に、次の色を選択するためのモーダルコンポーネント
 */
const ColorPicker = () => {
  const { isOpen, onColorSelected } = useUIStore((state) => state.modals.colorPicker);
  const closeColorPicker = useUIStore((state) => state.closeColorPicker);

  /**
   * 色を選択した時のハンドラ
   */
  const handleColorSelect = useCallback(
    (color: CardColor) => {
      if (onColorSelected) {
        onColorSelected(color);
      }
      closeColorPicker();
    },
    [onColorSelected, closeColorPicker]
  );

  /**
   * オーバーレイをクリックした時のハンドラ（モーダルを閉じる）
   */
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        closeColorPicker();
      }
    },
    [closeColorPicker]
  );

  /**
   * ESCキーでモーダルを閉じる
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeColorPicker();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeColorPicker]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="ColorPicker__overlay" onClick={handleOverlayClick}>
      <div className="ColorPicker__modal">
        <h2 className="ColorPicker__title">色を選択してください</h2>
        <p className="ColorPicker__description">ワイルドカードの次の色を選んでください</p>

        <div className="ColorPicker__options">
          <button
            className="ColorPicker__option ColorPicker__option--red"
            onClick={() => handleColorSelect(CardColor.RED)}
            aria-label="赤を選択"
          >
            <span className="ColorPicker__option-label">赤</span>
          </button>

          <button
            className="ColorPicker__option ColorPicker__option--blue"
            onClick={() => handleColorSelect(CardColor.BLUE)}
            aria-label="青を選択"
          >
            <span className="ColorPicker__option-label">青</span>
          </button>

          <button
            className="ColorPicker__option ColorPicker__option--yellow"
            onClick={() => handleColorSelect(CardColor.YELLOW)}
            aria-label="黄を選択"
          >
            <span className="ColorPicker__option-label">黄</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
